type WafScriptStatus = NonNullable<Window['Treatwell']['wafScript']>['status'];

export type WafTokenState = {
  sdkLoaded: boolean;
  scriptStatus: WafScriptStatus | 'unknown';
  scriptSettledMs: number | undefined;
  tokenRequest: 'ok' | 'error' | 'skipped';
  tokenError: string | undefined;
  sdkHasToken: boolean | undefined;
  hasTokenCookie: boolean;
  webdriver: boolean;
  pageAgeMs: number;
};

async function requestTokenFromSdk(): Promise<
  Pick<WafTokenState, 'tokenRequest' | 'tokenError'>
> {
  if (!window.AwsWafIntegration) {
    return {tokenRequest: 'skipped', tokenError: undefined};
  }
  try {
    const token = await window.AwsWafIntegration.getToken();
    return {
      tokenRequest: 'ok',
      tokenError:
        typeof token === 'string' && token !== ''
          ? undefined
          : 'no token returned',
    };
  } catch (error) {
    return {
      tokenRequest: 'error',
      tokenError: error instanceof Error ? error.message : String(error),
    };
  }
}

function readSdkHasToken() {
  try {
    return window.AwsWafIntegration?.hasToken();
  } catch {
    return undefined;
  }
}

function hasTokenCookie() {
  return (
    typeof document !== 'undefined' &&
    document.cookie.split('; ').some((c) => c.startsWith('aws-waf-token='))
  );
}

export async function ensureWafToken(): Promise<WafTokenState | undefined> {
  if (typeof window === 'undefined') return undefined;

  const {tokenRequest, tokenError} = await requestTokenFromSdk();
  const wafScript = window.Treatwell?.wafScript;

  return {
    sdkLoaded: Boolean(window.AwsWafIntegration),
    scriptStatus: wafScript?.status ?? 'unknown',
    scriptSettledMs: wafScript?.ms,
    tokenRequest,
    tokenError,
    sdkHasToken: readSdkHasToken(),
    hasTokenCookie: hasTokenCookie(),
    webdriver: typeof navigator !== 'undefined' && Boolean(navigator.webdriver),
    pageAgeMs: Math.round(performance.now()),
  };
}
